// File: src/components/onboarding/tutor/TutorOnboardingStepTwo.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "src/components/ui/Button";
import { useTutorOnboardingStore, TutorEducation } from "src/store/tutorOnboardingStore";
import EducationEntryForm from "./EducationEntryForm";
import { z } from "zod";
import { fieldErrors } from "src/constants/errorMessages";
import { PlusCircle } from 'lucide-react';
import ScrollToTop from "src/components/ScrollToTop";

// Validation schema using zod
const educationEntrySchema = z.object({
  country: z.string().min(1, { message: fieldErrors.email.invalid }),
  level: z.string().min(1, { message: fieldErrors.userType.required }),
  degree: z.string().min(1, { message: "Degree is required" }),
  field: z.string().min(1, { message: "Field of study is required" }),
  institution: z.string().min(1, { message: "Institution is required" }),
  year_of_completion: z.number().min(1950, { message: "Please select a valid year" }),
  gpa: z.number().optional(),
  gpa_total: z.number().optional(),
  is_custom_degree: z.boolean().optional(),
}).refine(
  (data) =>
    data.gpa === undefined ||
    data.gpa_total === undefined ||
    data.gpa <= data.gpa_total,
  {
    message: "GPA must not exceed the total GPA",
    path: ["gpa"],
  }
);

const createDefaultEducation = (): Partial<TutorEducation> => ({
  gpa: undefined,
  field: "",
  degree: "",
  country: "",
  institution: "",
  year_of_completion: 0,
  is_custom_degree: false,
  gpa_total: undefined,
  level: ""
});

const TutorOnboardingStepTwo: React.FC = () => {
  const { education, setEducation } = useTutorOnboardingStore();
  const [entries, setEntries] = useState<Partial<TutorEducation>[]>(education.length ? education : [createDefaultEducation()]);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [debouncedSearchQueries, setDebouncedSearchQueries] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  
  // Use ref to track if we're updating from store to prevent loops
  const isUpdatingFromStore = useRef(false);

  useEffect(() => {
    const timeouts: Record<string, NodeJS.Timeout> = {};
    Object.keys(searchQueries).forEach(key => {
      timeouts[key] = setTimeout(() => {
        setDebouncedSearchQueries(prev => ({ ...prev, [key]: searchQueries[key] || "" }));
      }, 300);
    });
    return () => Object.values(timeouts).forEach(clearTimeout);
  }, [searchQueries]);

  // Initialize entries from store education only once on mount
  useEffect(() => {
    if (education.length > 0 && !isUpdatingFromStore.current) {
      isUpdatingFromStore.current = true;
      setEntries(education);
      isUpdatingFromStore.current = false;
    }
  }, [education]);

  const updateSearchQuery = (fieldKey: string, query: string) => {
    setSearchQueries(prev => ({ ...prev, [fieldKey]: query }));
  };

  // Function to update store education
  const updateStoreEducation = useCallback((updatedEntries: Partial<TutorEducation>[]) => {
    if (isUpdatingFromStore.current) return;
    
    // Store all entries (including partial ones) to preserve user input
    // Validation will be done when determining if step is complete
    setEducation(updatedEntries as TutorEducation[]);
  }, [setEducation]);

  const handleChange = (index: number, field: keyof TutorEducation, value: any) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], [field]: value };
    setEntries(updated);
    
    // Update store immediately
    updateStoreEducation(updated);
  };

  const addMore = () => {
    if (entries.length < 3) {
      const updated = [...entries, createDefaultEducation()];
      setEntries(updated);
      // Update store to include the new empty entry
      updateStoreEducation(updated);
    }
  };

  const removeEntry = (index: number) => {
    const updated = entries.filter((_, i) => i !== index);
    setEntries(updated);
    // Update store immediately when removing entries
    updateStoreEducation(updated);
  };

  // Get already selected education levels (excluding current entry)
  const getSelectedLevels = (currentIndex: number): string[] => {
    return entries
      .map((entry, index) => index !== currentIndex ? entry.level : null)
      .filter((level): level is string => Boolean(level && level.trim()));
  };

  return (
    <>
      <ScrollToTop />
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>Education Details</h2>
          <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>Enter up to 3 highest qualifications, starting with the most recent.</p>
        </div>

        {entries.map((entry, index) => (
          <div key={index} className="relative">
            <EducationEntryForm
              index={index}
              entry={entry}
              debouncedSearchQueries={debouncedSearchQueries}
              updateSearchQuery={updateSearchQuery}
              onChange={handleChange}
              excludedLevels={getSelectedLevels(index)}
              allEntries={entries}
            />
            {entries.length > 1 && (
              <button
                type="button"
                className="absolute top-2 right-2 text-sm text-red-600 hover:underline"
                onClick={() => removeEntry(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        {entries.length < 3 && (
          <button
            type="button"
            onClick={addMore}
            className="inline-flex items-center gap-2 text-blue-600 font-normal hover:underline"
          >
            <PlusCircle size={18} /> Add Another Education Entry
          </button>
        )}
      </div>
    </>
  );
};

export default TutorOnboardingStepTwo;
